import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import Hls from "hls.js";

import { formatTime } from "../../format";

// Seeks are throttled so a fast sweep across the bar doesn't fetch a segment
// per pixel; moves closer than this are folded into one trailing seek.
const SEEK_THROTTLE_MS = 120;
// Ignore hover jitter — no point re-decoding for a sub-half-second move.
const MIN_SEEK_DELTA_S = 0.4;

/**
 * SeekPreview — the frame card that follows the pointer along the seek bar.
 *
 * The video pipeline has no sprite sheets, so the preview is a second, muted
 * `<video>` on its own tiny hls.js instance (lowest rendition, ~4s buffer)
 * that gets seeked to the hovered time; a paused video paints the frame it
 * sits on, so no canvas work is needed. The pipeline is built lazily on the
 * first hover and torn down with the component, and — like the ProgressBar —
 * everything per-move is imperative: no React state on the hot path.
 *
 * Imperative handle: `show(time, x, trackWidth)` / `hide()`.
 */
const SeekPreview = forwardRef(function SeekPreview({ src, aspectRatio }, ref) {
    const cardRef = useRef(null);
    const videoRef = useRef(null);
    const timeRef = useRef(null);

    const hlsRef = useRef(null);
    const startedRef = useRef(false);
    const lastSeekRef = useRef(-1);
    const pendingRef = useRef(null);
    const timerRef = useRef(null);

    // Tear the pipeline down with the source (and on unmount).
    useEffect(() => {
        return () => {
            clearTimeout(timerRef.current);
            hlsRef.current?.destroy();
            hlsRef.current = null;
            startedRef.current = false;
            lastSeekRef.current = -1;
        };
    }, [src]);

    const ensurePipeline = () => {
        const video = videoRef.current;
        if (!video || !src || hlsRef.current || startedRef.current) return;

        if (Hls.isSupported()) {
            const hls = new Hls({
                // Cheapest possible stream: start at the lowest rendition and let
                // the 160px element cap the level; buffer barely one fragment.
                startLevel: 0,
                capLevelToPlayerSize: true,
                maxBufferLength: 4,
                maxMaxBufferLength: 8,
                backBufferLength: 0,
                autoStartLoad: false,
                enableWorker: true,
                subtitleDisplay: false,
            });
            hlsRef.current = hls;
            hls.loadSource(src);
            hls.attachMedia(video);
        } else {
            // Native HLS (Safari): the element handles the manifest itself.
            video.src = src;
            startedRef.current = true;
        }
    };

    const seekTo = (time) => {
        const video = videoRef.current;
        if (!video) return;
        lastSeekRef.current = time;

        const hls = hlsRef.current;
        if (hls && !startedRef.current) {
            // First frame: start loading straight at the hovered position.
            startedRef.current = true;
            hls.startLoad(time);
            return;
        }

        video.currentTime = time;
    };

    const requestSeek = (time) => {
        if (Math.abs(time - lastSeekRef.current) < MIN_SEEK_DELTA_S) return;
        pendingRef.current = time;
        if (timerRef.current) return;

        seekTo(time);
        pendingRef.current = null;
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (pendingRef.current !== null) {
                const trailing = pendingRef.current;
                pendingRef.current = null;
                requestSeek(trailing);
            }
        }, SEEK_THROTTLE_MS);
    };

    useImperativeHandle(ref, () => ({
        show(time, x, trackWidth) {
            const card = cardRef.current;
            if (!card) return;

            ensurePipeline();

            // Centre on the pointer, but keep the whole card over the track.
            const half = card.offsetWidth / 2;
            const left = Math.max(half, Math.min(trackWidth - half, x));
            card.style.transform = `translateX(${left}px) translateX(-50%)`;
            if (timeRef.current) timeRef.current.textContent = formatTime(time);
            card.classList.add("is-visible");

            requestSeek(time);
        },
        hide() {
            cardRef.current?.classList.remove("is-visible");
        },
    }));

    // Loading feedback straight from the media events, no state round-trip.
    useEffect(() => {
        const video = videoRef.current;
        const card = cardRef.current;
        if (!video || !card) return;

        const onSeeking = () => card.classList.add("is-loading");
        const onSeeked = () => card.classList.remove("is-loading");
        // A seek requested before metadata was in lands on nothing — replay
        // the last hovered time once the element can actually seek.
        const onLoadedMetadata = () => {
            const target = lastSeekRef.current;
            if (target >= 0 && Math.abs(video.currentTime - target) > MIN_SEEK_DELTA_S) {
                video.currentTime = target;
            }
        };

        video.addEventListener("seeking", onSeeking);
        video.addEventListener("seeked", onSeeked);
        video.addEventListener("loadeddata", onSeeked);
        video.addEventListener("loadedmetadata", onLoadedMetadata);
        return () => {
            video.removeEventListener("seeking", onSeeking);
            video.removeEventListener("seeked", onSeeked);
            video.removeEventListener("loadeddata", onSeeked);
            video.removeEventListener("loadedmetadata", onLoadedMetadata);
        };
    }, []);

    // Landscape frames take the full 160px; portrait ones shrink in width so
    // the capped height still shows the true ratio.
    const ratio = aspectRatio > 0 ? aspectRatio : 16 / 9;
    const width = ratio < 1 ? Math.round(160 * ratio) : 160;

    return (
        <div
            ref={cardRef}
            className="player-seek-preview"
            style={{ "--preview-ratio": ratio, "--preview-width": `${width}px` }}
            aria-hidden="true"
        >
            <div className="player-seek-preview__frame">
                <video ref={videoRef} muted playsInline preload="none" tabIndex={-1} />
            </div>
            <span ref={timeRef} className="player-seek-preview__time" />
        </div>
    );
});

export default SeekPreview;
